import cn from 'classnames';
import React, { useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { closeSlotLayoutBuilderAction, updateSlotLayoutDraftAction } from 'editor/src/store/app/slice';
import createSlotLayoutOperation, {
  getProductOptions,
  isSlotLayoutDraftComplete,
  MIN_PRODUCTS,
} from 'editor/src/store/design/operation/createSlotLayoutOperation';
import updateSlotLayoutRowsOperation from 'editor/src/store/design/operation/updateSlotLayoutRowsOperation';
import { Rect, SlotLayoutDraft } from 'editor/src/store/design/types';
import computeSlotFrames, { getUsedSlotFormats } from 'editor/src/store/design/util/slotLayout/computeSlotFrames';
import defaultSlotRows from 'editor/src/store/design/util/slotLayout/defaultSlotRows';
import getSlotFolderAssets from 'editor/src/store/design/util/slotLayout/getSlotFolderAssets';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import useTr from 'editor/src/util/useTr';

import Button from 'editor/src/component/Button';
import IconArrowLeft from 'editor/src/component/Icon/IconArrowLeft';
import Input from 'editor/src/component/Input';
import TextInput from 'editor/src/component/TextInput';

import FileSetsSection from './FileSetsSection';
import LayoutsGrid from './LayoutsGrid';

import styles from './index.module.scss';

const MAX_PRODUCTS = 9;

/** A text input that turns read-only once the layers on the page depend on its value. */
function NameField({
  value,
  onChange,
  locked,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  locked: boolean;
  placeholder?: string;
}) {
  return locked ? (
    <Input type="text" value={value} disabled />
  ) : (
    <TextInput value={value} onChange={onChange} placeholder={placeholder} />
  );
}

/**
 * The form that turns a rows table and a few folders into a product layout. Opened from
 * "Add product layout"; on an existing layout only the grid can change, because the picklists
 * and picture sets are already wired into the layers on the page. A format the new grid
 * introduces still needs its folders, so those pickers stay open.
 */
function SlotLayoutBuilder() {
  const tr = useTr();
  const dispatch = useDispatch();
  const builder = useSelector((state) => state.app.slotLayoutBuilder, shallowEqual);
  const folders = useSelector((state) => state.collections.folders);
  const existingFrames = useSelector((state) =>
    builder?.layoutId
      ? state.design.designData?.spreads[builder.spreadIndex]?.slotLayouts?.find((l) => l.id === builder.layoutId)
          ?.frames
      : undefined,
  );
  // Typed freely and taken on blur or Enter: clamping each keystroke turns "6" into "2" on
  // backspace and "24" into the maximum before the second digit can be read.
  const [maxProductsText, setMaxProductsText] = useState(String(builder?.draft.maxProducts ?? ''));

  const formats = useMemo(() => (builder ? getUsedSlotFormats(builder.draft.rows) : []), [builder?.draft.rows]);
  if (!builder) {
    return null;
  }

  const { draft, layoutId, spreadIndex } = builder;
  const editing = !!layoutId;
  const productOptions = getProductOptions(draft);
  const set = (patch: Partial<SlotLayoutDraft>) => dispatch(updateSlotLayoutDraftAction(patch));
  const close = () => dispatch(closeSlotLayoutBuilderAction());

  // Every format that gets a frame needs a folder that has arrived and holds a picture, or the
  // slot stays empty. On an existing layout only the frames the new grid adds draw from the
  // folders, so a folder the host no longer returns cannot hold the grid hostage.
  const existingIds = new Set((existingFrames ?? []).map((frame) => frame.id));
  const framedFormats = editing
    ? getUsedSlotFormats(draft.rows).filter((columns) =>
        computeSlotFrames(draft.rows, draft.area, draft.gap).some(
          (frame) => frame.columns === columns && !existingIds.has(frame.id),
        ),
      )
    : formats;
  const foldersComplete = draft.fileSets.every((fileSet) =>
    framedFormats.every((columns) => {
      const folderRef = fileSet.folders[columns];
      return !!folderRef && getSlotFolderAssets(folders[folderRef.folderId], folderRef).length > 0;
    }),
  );
  const complete = isSlotLayoutDraftComplete(draft) && foldersComplete;

  const commitMaxProducts = () => {
    const typed = Math.round(Number(maxProductsText));
    const maxProducts =
      maxProductsText.trim() && Number.isFinite(typed)
        ? Math.min(MAX_PRODUCTS, Math.max(MIN_PRODUCTS, typed))
        : draft.maxProducts;
    setMaxProductsText(String(maxProducts));
    if (maxProducts !== draft.maxProducts) {
      // Counts that stay keep the rows the author set; only the new ones get defaults.
      const defaults = defaultSlotRows(maxProducts, Math.max(1, ...draft.rows.map((r) => r.length)));
      set({ maxProducts, rows: defaults.map((row, index) => draft.rows[index] ?? row) });
    }
  };

  const setArea = (key: keyof Rect, value: string) => {
    const number = parseFloat(value);
    if (!Number.isNaN(number)) {
      set({ area: { ...draft.area, [key]: number } });
    }
  };

  const submit = () => {
    if (editing) {
      dispatch(
        updateSlotLayoutRowsOperation(spreadIndex, layoutId, {
          rows: draft.rows,
          area: draft.area,
          gap: draft.gap,
          fileSets: draft.fileSets,
        }),
      );
    } else {
      dispatch(createSlotLayoutOperation(spreadIndex, draft));
    }
    close();
  };

  return (
    <div className={cn(styles.SlotLayoutBuilder, 'cy-slot-layout-builder')}>
      <div className={styles.header}>
        <Button
          transparent
          noPadding
          onClick={close}
          className="cy-slot-layout-back"
          ariaLabel={tr('editor-back', 'Back')}
        >
          <IconArrowLeft />
        </Button>
        <div className={styles.title}>{tr('editor-slot-layout-title', 'Product layout')}</div>
      </div>
      <p className={styles.help}>
        {editing
          ? tr(
              'editor-slot-layout-help-edit',
              'Change which rows each count uses. Frames that stay keep their layers; new frames get a copy of the first slot.',
            )
          : tr(
              'editor-slot-layout-help',
              'The buyer picks how many products the page shows, then fills a slot per product. ' +
                'Set a row arrangement for each count, pick the folders the slots offer, and generate. ' +
                'Then design the first slot of each format and apply it to the rest.',
            )}
      </p>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>{tr('editor-slot-layout-section-choice', "Buyer's choice")}</div>
        <label className={styles.field}>
          <span>{tr('editor-slot-layout-name', 'Picklist name')}</span>
          <NameField value={draft.name} onChange={(name) => set({ name })} locked={editing} />
        </label>
        <label className={styles.field}>
          <span>{tr('editor-slot-layout-max-products', 'Up to how many products')}</span>
          <Input
            type="number"
            disabled={editing}
            value={maxProductsText}
            onChange={(e) => setMaxProductsText(e.target.value)}
            onBlur={commitMaxProducts}
            onKeyDown={(e) => e.key === 'Enter' && commitMaxProducts()}
          />
        </label>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>{tr('editor-slot-layout-section-layouts', 'Rows per count')}</div>
        <LayoutsGrid rows={draft.rows} onChange={(rows) => set({ rows })} />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>{tr('editor-slot-layout-section-area', 'Area (mm)')}</div>
        <div className={styles.areaFields}>
          {(
            [
              ['x', tr('editor-left', 'Left')],
              ['y', tr('editor-top', 'Top')],
              ['width', tr('editor-width', 'Width')],
              ['height', tr('editor-height', 'Height')],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className={styles.areaField}>
              <span>{label}</span>
              <Input type="number" value={draft.area[key]} onChange={(e) => setArea(key, e.target.value)} />
            </label>
          ))}
          <label className={styles.areaField}>
            <span>{tr('editor-slot-layout-gap', 'gap')}</span>
            <Input
              type="number"
              value={draft.gap}
              onChange={(e) => set({ gap: Math.max(0, parseFloat(e.target.value) || 0) })}
            />
          </label>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>{tr('editor-slot-layout-section-options', 'Choice per product')}</div>
        <p className={styles.hint}>
          {tr(
            'editor-slot-layout-options-hint',
            'Optional: a picklist the buyer answers for each product, such as Campaign / Leasing / Advert. Leave empty for none.',
          )}
        </p>
        <label className={styles.field}>
          <span>{tr('editor-slot-layout-product-label', 'Picklist name (numbered per product)')}</span>
          <NameField
            value={draft.productLabel}
            onChange={(productLabel) => set({ productLabel })}
            placeholder={tr('editor-slot-layout-default-product', 'Product')}
            locked={editing}
          />
        </label>
        <label className={styles.field}>
          <span>{tr('editor-slot-layout-options', 'Options, comma-separated')}</span>
          <NameField
            value={draft.productOptions.join(', ')}
            onChange={(value) => set({ productOptions: value.split(',').map((s) => s.trimStart()) })}
            placeholder={tr('editor-slot-layout-options-placeholder', 'Campaign, Leasing, Advert')}
            locked={editing}
          />
        </label>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>{tr('editor-slot-layout-section-pictures', 'Pictures in each slot')}</div>
        <p className={styles.hint}>
          {tr(
            'editor-slot-layout-pictures-hint',
            'One folder per slot format. Upload each product as one PDF and keep "Separate folders" on: page 1 lands in one folder and page 2 in another, under the same names.',
          )}
        </p>
        <FileSetsSection
          fileSets={draft.fileSets}
          formats={formats}
          productOptions={productOptions}
          onChange={(fileSets) => set({ fileSets })}
          disabled={editing}
        />
      </div>

      <div className={styles.footer}>
        <Button secondary onClick={close} className="cy-slot-layout-cancel">
          {tr('editor-slot-layout-cancel', 'Cancel')}
        </Button>
        <Button primary disabled={!complete} onClick={submit} className="cy-slot-layout-submit">
          {editing ? tr('editor-slot-layout-save', 'Save grid') : tr('editor-slot-layout-generate', 'Generate')}
        </Button>
      </div>
    </div>
  );
}

export default React.memo(SlotLayoutBuilder);
